import { createEventDispatcher, onDestroy, onMount } from 'svelte';
import { get, writable, type Writable } from 'svelte/store';

type TriggerObserverEvents = {
	enter: { layoutId?: string; attachedElement: Element | null; direction: number };
	leave: { layoutId?: string; attachedElement: Element | null; direction: number };
};

type CanvasAttachedElement = HTMLElement & {
	zoomIn: () => void;
	zoomOut: () => void;
	goHome: () => void;
	goToTarget: (e: any) => void;
	goFullscreen: () => void;
};

export type CallbackObject = {
	enter: (attachedElement: Element | null) => void;
	leave: (attachedElement: Element | null) => void;
};

export function useTriggerObserver({
	shouldObserve,
	debug,
	layoutId,
	rootMargin,
	triggerType = undefined,
	triggerDirection = 'both',
	zoomIn = false,
	zoomOut = false,
	goHome = false,
	goToTarget = undefined,
	goFullscreen = false,
}: {
	shouldObserve?: boolean;
	debug?: boolean;
	layoutId?: string;
	rootMargin?: number;
	triggerType?: 'enter' | 'leave' | undefined;
	triggerDirection?: 'top' | 'bottom' | 'both';
	zoomIn?: boolean;
	zoomOut?: boolean;
	goHome?: boolean;
	goToTarget?: string;
	goFullscreen?: boolean;
}) {
	/**
	 * Variables
	 */
	const element: Writable<HTMLElement | null> = writable(null);
	const hasPredefinedFunctions = zoomIn || zoomOut || goHome || goToTarget || goFullscreen;
	const callbackFunction: CallbackObject = {
		enter: () => {},
		leave: () => {},
	};
	const predefinedFnCount = [
		zoomIn,
		zoomOut,
		goHome,
		goToTarget,
		goFullscreen
	].filter(Boolean).length;
	let observer: IntersectionObserver | undefined = undefined;
	let hasEntered = false;
	let attachedElement: HTMLElement | null = null;
	let lastElTop = 0;
	/**
	 * Composables
	 */
	const dispatch = createEventDispatcher<TriggerObserverEvents>();

	/**
	 * Methods
	 */
	const setTriggerCallback = (cb: Function, type: 'enter' | 'leave' = 'enter'): void => {
		if (type === 'enter') {
			(callbackFunction as any).enter = cb;
		} else {
			(callbackFunction as any).leave = cb;
		}
	};

	const getCallback = (): Object => {
		return callbackFunction;
	};

	// helper to parse go-to-target string
	const parseGoToTarget = (target: string): { width: number; height: number; x: number; y: number } => {
		const targetArray = target.split(',').map(Number);

		if (targetArray.length !== 4 || targetArray.some(isNaN)) {
			throw new Error(`Invalid go-to target string: "${target}"`);
		}

		const [width, height, x, y] = targetArray;

		return { width, height, x, y };
	};

	// helper to trigger canvas functions
	const triggerCanvasFunction = (canvas: CanvasAttachedElement) => {
		if (zoomIn) {
			canvas.zoomIn();
		}
		if (zoomOut) {
			canvas.zoomOut();
		}
		if (goHome) {
			canvas.goHome();
		}
		if (goToTarget) {
			canvas.goToTarget(parseGoToTarget(goToTarget));
		}
		if (goFullscreen) {
			canvas.goFullscreen();
		}
	}

	// helper to decide if the trigger should fire based on direction
	const shouldTriggerBasedOnDirection = (direction: 1 | -1) => {
		if (triggerDirection === 'top') return direction === -1;
		if (triggerDirection === 'bottom') return direction === 1;
		return true; // 'both'
	};

	// helper to trigger canvas functions based on direction
	const handleTriggerCanvasFunction = (direction: 1 | -1) => {
		if (
			attachedElement?.tagName === 'RF-CANVAS' &&
			hasPredefinedFunctions &&
			shouldTriggerBasedOnDirection(direction)
		) {
			triggerCanvasFunction(attachedElement as CanvasAttachedElement);
		}
	};
	/**
	 * Lifecycle
	 */
	onMount(() => {
		const currentElement = get(element);
		if (!currentElement || !shouldObserve) return;
		// if layoutId is defined, get the element with the id
		if (layoutId) {
			attachedElement = document.getElementById(layoutId) as HTMLElement;
			// warn if the element is not found
			if (!attachedElement) {
				console.warn('No element found with the id', layoutId);
			}
		}

		if (debug && predefinedFnCount > 1) {
			console.warn(`${layoutId} : RfObserverTrigger: More than one predefined function is set, it can result in unexpected behavior.`);
		}
		// if the element is not null and shouldObserve is true, create the observer
		if (currentElement && shouldObserve) {
			// TODO: check if the attached element is in the DOM and ready
			observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						// get the element top position to determine the direction
						const elTop = entry.boundingClientRect.top;
						let direction: 1 | -1 = 1;

						if (lastElTop) {
							direction = elTop < lastElTop ? 1 : -1;
						}

						if (entry.isIntersecting) {
							// if the element has not entered yet, set hasEntered to true
							if (!hasEntered) {
								hasEntered = true;
							}
							dispatch('enter', { layoutId, attachedElement, direction });
							// if the element is a rf-canvas and has any predefined functions, trigger them
							if (triggerType === 'enter') {
								handleTriggerCanvasFunction(direction);
							}
							if (debug) {
								console.log('\x1b[42m\x1b[30m[Debug]\x1b[0m \x1b[32mmarker entered\x1b[32m', { layoutId, attachedElement, direction });
							}
						} else {
							// prevent leaving the element if it has not entered yet
							if (hasEntered) {
								dispatch('leave', { layoutId, attachedElement, direction });
								// if the element is a rf-canvas and has any predefined functions, trigger them
								if (triggerType === 'leave') {
									handleTriggerCanvasFunction(direction);
								}
								if (debug) {
									console.log('\x1b[42m\x1b[30m[Debug]\x1b[0m \x1b[32mmarker left\x1b[32m', { layoutId, attachedElement, direction });
								}
							}
						}
						lastElTop = elTop;
					});
				},
				{
					rootMargin: `0px 0px ${rootMargin ? rootMargin + '%' : '0px'} 0px`,
				}
			);
			observer.observe(currentElement);
		}
	});

	onDestroy(() => {
		observer?.disconnect();
	});

	return { element, setTriggerCallback, getCallback };
}
